/**
 * KDS Station Layout Component
 * 
 * Shared layout for all KDS stations (Barista, Kitchen, Juicer, Counter)
 * Provides:
 * - Auto-reconnect with WebSocket/polling fallback
 * - Offline caching
 * - Sound notifications for new orders
 * - Connection status indicators
 * - Consistent header/footer
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RefreshCw, 
  LogOut, 
  Sun, 
  Moon,
  Package,
  Wifi,
  WifiOff,
  CloudOff,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import KDSOrderCard from '@/components/kds/KDSOrderCard';
import { printStationOrder } from '@/utils/usbPrint';
import { api } from '@/utils/apiClient';
import useKDSConnection, { ConnectionStatus } from '@/hooks/useKDSConnection';

export default function KDSStationLayout({ 
  station,           // 'barista', 'kitchen', 'juicer', 'counter'
  stationName,       // Display name: 'Barista Station'
  stationIcon: Icon, // Lucide icon component
  iconColorClass,    // Tailwind gradient classes for icon bg
  defaultDeviceId    // Default device ID: 'KDS-BAR-01'
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('device') || defaultDeviceId;
  
  // Use the KDS connection hook with auto-reconnect and caching
  const { 
    orders, 
    isConnected, 
    isOffline, 
    connectionStatus, 
    lastSync,
    refetch 
  } = useKDSConnection(station);
  
  const [isDark, setIsDark] = useState(false);
  const [session, setSession] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load session and theme
  useEffect(() => {
    const savedSession = localStorage.getItem('kds-session');
    if (savedSession) {
      setSession(JSON.parse(savedSession));
    }
    
    const savedTheme = localStorage.getItem('kds-dark-mode');
    if (savedTheme === 'true') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('kds-dark-mode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('kds-dark-mode', 'false');
    }
  };

  // Manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  // Heartbeat to backend
  useEffect(() => {
    const heartbeat = async () => {
      try {
        await api.post('/api/devices/heartbeat', { device_id: deviceId });
      } catch (error) {
        console.error('Heartbeat error:', error);
      }
    };
    
    heartbeat();
    const interval = setInterval(heartbeat, 60000);
    return () => clearInterval(interval);
  }, [deviceId]);

  // Handle item ready
  const handleItemReady = async (orderId, itemIndex) => {
    try {
      await api.post('/api/kds/item/ready', {
        order_id: orderId,
        item_index: itemIndex,
        status: 'ready',
        staff_id: session?.staff_id,
        device_id: deviceId
      });
      
      toast.success('Item marked ready');
      refetch();
    } catch (error) {
      console.error('Error marking item ready:', error);
      toast.error('Failed to update item');
    }
  };

  // Handle print order ticket
  const handlePrintOrder = async (order) => {
    try {
      const result = await printStationOrder(order, station);
      if (result.success) {
        toast.success('Ticket sent to printer');
      } else {
        toast.error(result.error || 'Print failed');
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print ticket');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      if (session?.session_id) {
        await api.post('/api/device-sessions/logout', {
          device_id: deviceId,
          session_id: session.session_id
        });
      }
      
      localStorage.removeItem('kds-session');
      localStorage.removeItem('kds-staff');
      localStorage.removeItem('kds-device');
      navigate('/kds/login?device=' + deviceId);
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/kds/login?device=' + deviceId);
    }
  };

  // Get connection status indicator
  const getConnectionIndicator = () => {
    if (isOffline) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
          <WifiOff className="w-4 h-4" />
          <span className="text-xs font-medium">Offline</span>
        </div>
      );
    }
    
    switch (connectionStatus) {
      case ConnectionStatus.CONNECTED:
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
            <Wifi className="w-4 h-4" />
            <span className="text-xs font-medium">Live</span>
          </div>
        );
      case ConnectionStatus.POLLING:
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-xs font-medium">Syncing</span>
          </div>
        );
      case ConnectionStatus.RECONNECTING:
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs font-medium">Reconnecting...</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500">
            <CloudOff className="w-4 h-4" />
            <span className="text-xs font-medium">Connecting...</span>
          </div>
        );
    }
  };

  // Count stats
  const totalItems = orders.reduce((sum, o) => sum + (o.item_count || o.items?.length || 0), 0);
  const pendingItems = orders.reduce((sum, o) => 
    sum + (o.items?.filter(i => i.kds_status === 'pending').length || 0), 0);

  return (
    <div className={`min-h-screen ${isDark ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${iconColorClass} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 dark:text-white">{stationName}</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">{deviceId}</p>
              </div>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Connection Status */}
            {getConnectionIndicator()}
            
            {/* Last Sync Time (if offline) */}
            {lastSync && isOffline && (
              <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">
                Last sync: {lastSync.toLocaleTimeString()}
              </span>
            )}
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Logout */}
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className={`px-4 py-2 ${iconColorClass.replace('bg-gradient-to-br', 'bg-opacity-10')} bg-opacity-10 flex items-center gap-6 text-sm`}>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-500" />
            <span className="text-gray-600 dark:text-gray-300">
              <strong className="text-gray-900 dark:text-white">{orders.length}</strong> orders
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-gray-500" />
            <span className="text-gray-600 dark:text-gray-300">
              <strong className="text-gray-900 dark:text-white">{pendingItems}</strong> items to prepare
            </span>
          </div>
        </div>
        
        {/* Offline Warning Banner */}
        {isOffline && (
          <div className="px-4 py-2 bg-red-500 text-white flex items-center justify-center gap-2 text-sm">
            <WifiOff className="w-4 h-4" />
            <span className="font-medium">You are offline - Showing cached orders from {lastSync?.toLocaleTimeString() || 'earlier'}</span>
          </div>
        )}
      </header>

      {/* Main Content - Order Grid */}
      <main className="p-4">
        <AnimatePresence mode="popLayout">
          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className={`w-24 h-24 rounded-full ${iconColorClass} bg-opacity-10 flex items-center justify-center mb-6`}>
                <Icon className="w-12 h-12 text-gray-400" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">No Orders</h2>
              <p className="text-gray-500 dark:text-gray-400">Waiting for orders to come in...</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {orders.map((order, index) => (
                <motion.div
                  key={order.id || order.order_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <KDSOrderCard
                    order={order}
                    station={station}
                    onItemReady={(itemIndex) => handleItemReady(order.id || order.order_id, itemIndex)}
                    onPrint={() => handlePrintOrder(order)}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
